import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { Panel, SectionTitle, TactLoader } from "../components/Tactical";
import { toast } from "sonner";
import { Copy, Crown, Plus, Share2, Users, Zap, Trophy, Check } from "lucide-react";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

export function ByolHome() {
  const { user } = useAuth();
  const { t } = useT();
  const [mine, setMine] = useState(null);

  useEffect(() => {
    if (!user || user === false) { setMine([]); return; }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/byol/leagues/mine");
        if (alive) setMine(data);
      } catch (e) { if (alive) setMine([]); }
    })();
    return () => { alive = false; };
  }, [user]);

  return (
    <div className="space-y-6" data-testid="byol-home">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED] mb-1">{t("byol.kicker")}</div>
          <h1 className="font-heading text-3xl sm:text-4xl uppercase tracking-wider">{t("byol.title")}</h1>
          <div className="font-mono text-[10px] text-[#71717A] mt-1">{t("byol.subtitle")}</div>
        </div>
        <Link
          to="/byol/new"
          data-testid="byol-create-btn"
          className="inline-flex items-center gap-2 bg-[#7C3AED] text-white font-heading uppercase tracking-widest px-5 py-2.5 text-xs hover:bg-[#6D28D9] transition-colors shadow-[0_8px_20px_rgba(124,58,237,0.3)]"
        >
          <Plus className="w-3.5 h-3.5" /> {t("byol.create")}
        </Link>
      </div>

      {mine === null ? <TactLoader label={t("byol.loading")} /> :
        mine.length === 0 ? (
          <Panel className="font-mono text-[#71717A] text-sm" testId="byol-empty">
            {t("byol.empty")}
          </Panel>
        ) : (
          <div className="space-y-3" data-testid="byol-list">
            {mine.map((lg) => {
              const pct = Math.round((lg.player_count / lg.capacity) * 100);
              const started = lg.status !== "pending";
              return (
                <Link
                  key={lg.id}
                  to={started ? `/leagues/${lg.id}` : `/byol/${lg.id}`}
                  data-testid={`byol-row-${lg.id}`}
                  className="block bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-l-2 border-l-[#7C3AED] px-5 py-4 hover:border-[#7C3AED] transition-colors"
                >
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      {lg.captain_id === user?.id && <Crown className="w-4 h-4 text-[#F59E0B] flex-shrink-0" />}
                      <div className="min-w-0">
                        <div className="font-heading text-lg uppercase text-[#09090B] dark:text-[#F4F4F5] truncate">{lg.name}</div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
                          {t("byol.captain")}: {lg.captain_username || "—"} · {t("byol.created")} {fmt(lg.created_at)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className={`font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 border ${started ? "border-[#34C759] text-[#34C759]" : "border-[#EA580C] text-[#EA580C]"}`}>
                        {t(`byol.status.${lg.status}`)}
                      </span>
                      <div className="flex items-center gap-2 min-w-[140px]">
                        <div className="flex-1 h-1.5 bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30]">
                          <div className="h-full bg-[#7C3AED]" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="font-mono text-xs text-[#71717A] whitespace-nowrap">{lg.player_count}/{lg.capacity}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
    </div>
  );
}

export function ByolCreate() {
  const nav = useNavigate();
  const { t } = useT();
  const [name, setName] = useState("");
  const [size, setSize] = useState(10);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return toast.error(t("err.required"));
    setBusy(true);
    try {
      const { data } = await api.post("/byol/leagues", { name: name.trim(), size_limit: size });
      toast.success(t("byol.new.toast"));
      nav(`/byol/${data.id}`, { replace: true });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-md space-y-6" data-testid="byol-create-page">
      <SectionTitle kicker={t("byol.kicker")}>{t("byol.new.title")}</SectionTitle>
      <form onSubmit={submit} className="space-y-4" data-testid="byol-create-form">
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">{t("byol.new.nameLabel")}</label>
          <input
            data-testid="byol-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={60}
            placeholder={t("byol.new.namePlaceholder")}
            className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-3 text-[#09090B] dark:text-[#F4F4F5] font-mono focus:outline-none focus:ring-1 focus:ring-[#7C3AED]"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">{t("byol.new.sizeLabel")}</label>
          <div className="flex gap-2">
            {[2, 4, 6, 8, 10].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setSize(n)}
                data-testid={`byol-size-${n}`}
                className={`flex-1 border px-3 py-3 font-heading text-lg transition-colors ${
                  size === n
                    ? "border-[#7C3AED] text-[#7C3AED] bg-[#7C3AED]/5"
                    : "border-[#E4E4E7] dark:border-[#2A2A30] hover:border-[#7C3AED]/50 text-[#09090B] dark:text-[#F4F4F5]"
                }`}
              >
                {n}
              </button>
            ))}
          </div>
          <div className="font-mono text-[10px] text-[#71717A] mt-2">{t("byol.captainView.autoStart", { max: size })}</div>
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="byol-create-submit"
          className="w-full bg-[#7C3AED] text-white font-heading uppercase tracking-widest py-3 text-sm hover:bg-[#6D28D9] transition-colors disabled:opacity-50"
        >
          {busy ? t("byol.new.submitting") : t("byol.new.submit")}
        </button>
      </form>
    </div>
  );
}

export function ByolCaptainView() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const [lg, setLg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/leagues/${id}`);
      setLg(data);
    } catch (e) {
      setErr(e?.response?.status === 404 ? "notfound" : "error");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); const iv = setInterval(refresh, 10000); return () => clearInterval(iv); }, [id]);

  const inviteLink = lg?.invite_code ? `${window.location.origin}/join/${lg.invite_code}` : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      toast.success(t("byol.captainView.copied"));
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { toast.error(formatApiError(e) || t("err.generic")); }
  };

  const shareLink = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: lg.name, text: t("byol.invite.subtitle"), url: inviteLink }); }
      catch (e) { /* cancelled */ }
    } else { copyLink(); }
  };

  const startLeague = async () => {
    if (!window.confirm(`${lg.player_count} / ${lg.capacity} — ${t("byol.captainView.startNow")}?`)) return;
    setBusy(true);
    try {
      await api.post(`/byol/leagues/${id}/start`);
      toast.success(t("byol.captainView.goLeague"));
      nav(`/leagues/${id}`, { replace: true });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (loading) return <TactLoader label={t("byol.loading")} />;
  if (err === "notfound") return (
    <Panel className="font-mono text-[#71717A] text-center py-12">
      {t("err.notFound")}
    </Panel>
  );
  if (!lg) return null;

  const isCaptain = user?.id === lg.captain_id;
  const pct = Math.round(((lg.player_count || 0) / lg.capacity) * 100);
  const canStart = isCaptain && lg.status === "pending" && lg.player_count >= 2;

  if (lg.status !== "pending") {
    // Already started — redirect into standard league view
    nav(`/leagues/${id}`, { replace: true });
    return null;
  }

  return (
    <div className="space-y-6" data-testid="byol-captain-view">
      <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-t-2 border-t-[#7C3AED] p-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED] mb-1">{t("home.private")}</div>
        <h1 className="font-heading text-3xl uppercase tracking-wider text-[#09090B] dark:text-[#F4F4F5]" data-testid="byol-name">{lg.name}</h1>
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mt-2">
          {t("byol.captain")}: <span className="text-[#09090B] dark:text-[#F4F4F5]">{lg.players_detail?.find((p) => p.id === lg.captain_id)?.username || "—"}</span> · {t("dash.status")}: <span className="text-[#EA580C]">{t(`byol.status.${lg.status}`)}</span>
        </div>

        {/* Roster progress */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]"><Users className="inline w-3 h-3 mr-1" /> {t("byol.captainView.roster")}</span>
            <span className="font-heading text-xl text-[#7C3AED]" data-testid="byol-player-count">{lg.player_count}/{lg.capacity}</span>
          </div>
          <div className="h-2 bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30]">
            <div className="h-full bg-gradient-to-r from-[#7C3AED] to-[#EA580C]" style={{ width: `${pct}%` }} />
          </div>
        </div>

        {/* Invite section */}
        <div className="mt-6 bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] mb-2">{t("byol.captainView.inviteTitle")}</div>
          <div className="flex items-center gap-2 flex-wrap">
            <code className="flex-1 min-w-[200px] font-mono text-xs text-[#09090B] dark:text-[#F4F4F5] bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 break-all" data-testid="byol-invite-link">{inviteLink}</code>
            {isCaptain && (
              <>
                <button onClick={copyLink} data-testid="byol-copy-btn" className="inline-flex items-center gap-1 border border-[#7C3AED] text-[#7C3AED] font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-2 hover:bg-[#7C3AED] hover:text-white transition-colors">
                  {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {copied ? t("byol.captainView.copied") : t("byol.captainView.copy")}
                </button>
                <button onClick={shareLink} data-testid="byol-share-btn" className="inline-flex items-center gap-1 bg-[#7C3AED] text-white font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-2 hover:bg-[#6D28D9] transition-colors">
                  <Share2 className="w-3 h-3" /> {t("byol.captainView.share")}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Start button */}
        {isCaptain && (
          <div className="mt-6">
            <button
              onClick={startLeague}
              disabled={!canStart || busy}
              data-testid="byol-start-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#EA580C] text-white font-heading uppercase tracking-widest py-3 text-sm hover:bg-[#C2410C] transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_8px_20px_rgba(234,88,12,0.3)]"
            >
              <Zap className="w-4 h-4" /> {busy ? t("byol.captainView.starting") : canStart ? `${t("byol.captainView.startNow")} (${lg.player_count})` : t("byol.captainView.minPlayers", { n: 2 })}
            </button>
          </div>
        )}
      </div>

      {/* Roster list */}
      <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-6" data-testid="byol-roster">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-4 h-4 text-[#7C3AED]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED]">{t("byol.captainView.roster")}</span>
        </div>
        {!lg.players_detail?.length ? <div className="font-mono text-[#71717A] text-sm">{t("byol.empty")}</div> : (
          <ul className="space-y-1">
            {lg.players_detail.map((p, i) => (
              <li key={p.id} className="flex items-center justify-between border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2" data-testid={`byol-player-${p.id}`}>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] text-[#71717A]">#{i + 1}</span>
                  {p.id === lg.captain_id && <Crown className="w-3 h-3 text-[#F59E0B]" />}
                  <Link to={`/players/${encodeURIComponent(p.username)}`} className="font-heading uppercase text-[#09090B] dark:text-[#F4F4F5] hover:text-[#7C3AED]">{p.username}</Link>
                </div>
                <span className="font-mono text-[10px] text-[#71717A]">{p.game_id}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function ByolInviteLanding() {
  const { code } = useParams();
  const { user } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const [search] = useSearchParams();
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/byol/invites/${code}`);
        if (alive) setPreview(data);
      } catch (e) { if (alive) setErr(e?.response?.status === 404 ? "notfound" : "error"); }
    })();
    return () => { alive = false; };
  }, [code]);

  const join = async () => {
    if (!user || user === false) {
      nav(`/register?next=/join/${code}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/byol/invites/${code}/join`);
      toast.success(t("byol.invite.joined"));
      nav(`/byol/${data.league_id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (err === "notfound") return (
    <Panel className="max-w-md mx-auto font-mono text-[#71717A] text-center py-12" testId="byol-invite-invalid">
      {t("err.notFound")}
    </Panel>
  );
  if (!preview) return <TactLoader label={t("byol.loading")} />;

  const isFull = preview.player_count >= preview.size_limit;
  const alreadyStarted = preview.status !== "pending";
  const canJoin = !isFull && !alreadyStarted;

  return (
    <div className="max-w-md mx-auto" data-testid="byol-invite-landing">
      <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-t-2 border-t-[#7C3AED] p-8 text-center">
        <Crown className="w-10 h-10 text-[#F59E0B] mx-auto mb-4 drop-shadow-[0_0_12px_rgba(245,158,11,0.5)]" />
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED] mb-2">{t("byol.invite.title")}</div>
        <h1 className="font-heading text-3xl uppercase tracking-wider text-[#09090B] dark:text-[#F4F4F5] mb-3" data-testid="byol-invite-name">{preview.name}</h1>
        <div className="font-mono text-xs text-[#71717A] mb-6">
          {t("byol.captain")}: <span className="text-[#09090B] dark:text-[#F4F4F5]">{preview.captain_username}</span> · {preview.player_count}/{preview.size_limit}
        </div>

        {alreadyStarted ? (
          <div className="font-mono text-sm text-[#E11D48] mb-4" data-testid="byol-invite-closed">{t("byol.captainView.started")}</div>
        ) : isFull ? (
          <div className="font-mono text-sm text-[#EA580C] mb-4" data-testid="byol-invite-full">{t("byol.invite.full")}</div>
        ) : null}

        <button
          onClick={join}
          disabled={!canJoin || busy}
          data-testid="byol-invite-join-btn"
          className="w-full bg-[#7C3AED] text-white font-heading uppercase tracking-widest py-3 text-sm hover:bg-[#6D28D9] transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_8px_20px_rgba(124,58,237,0.3)]"
        >
          {busy ? t("byol.invite.joining") : !user || user === false ? t("byol.invite.registerBtn") : t("byol.invite.joinBtn")}
        </button>
      </div>
    </div>
  );
}
